import { Command, EditorState } from "prosemirror-state";
import { useCallback, useMemo } from "react";
import { setNodeAttributes } from "../commands/setNodeAttributes";
import {
	doesSupportTextAlign,
	isTextAlignNodeAttributes,
	SupportedTextAlignOptions,
	TextAlignNodeAttributesPart
} from "../schema/nodes/attributes/textAlign";

export type TextAlignToolHandlers = {
	alignLeftHandler(): void;
	alignRightHandler(): void;
	alignCenterHandler(): void;
	alignJustifyContentHandler(): void;
	isTextAlignActive(alignValue: SupportedTextAlignOptions): boolean;
};

export function useTextAlignTools(
	editorState: EditorState,
	applyCommand: (command: Command) => void,
	focus?: () => void
): TextAlignToolHandlers {
	const isActive = useCallback(
		(alignValue: SupportedTextAlignOptions): boolean => {
			const {
				selection: {
					from,
					to
				},
				doc
			} = editorState;

			const setValues = new Set<SupportedTextAlignOptions>();

			doc.nodesBetween(
				from,
				to,
				(node): boolean | undefined => {
					if (!doesSupportTextAlign(node.type.spec)) {
						return undefined;
					}

					if (isTextAlignNodeAttributes(node.attrs)) {
						const newValue: SupportedTextAlignOptions = node.attrs
							.textAlign;

						if (!setValues.has(newValue)){
							setValues.add(newValue);
						}
					}

					return undefined;
				}
			);

			return setValues.size === 1 && setValues.has(alignValue);
		},
		[editorState]
	);

	return useMemo(
		(): TextAlignToolHandlers => {
			const alignHandler = (alignValue: SupportedTextAlignOptions): void => {
				applyCommand(
					setNodeAttributes<TextAlignNodeAttributesPart>(
						existingAttrs => ({
							...existingAttrs,
							textAlign: alignValue
						}),
						doesSupportTextAlign,
						attrs => isTextAlignNodeAttributes(attrs) && attrs.textAlign === alignValue
					)
				);
				focus?.();
			};

			return {
				alignLeftHandler: () => alignHandler("left"),
				alignRightHandler: () => alignHandler("right"),
				alignCenterHandler: () => alignHandler("center"),
				alignJustifyContentHandler: () => alignHandler("justify"),
				isTextAlignActive: isActive
			};
		},
		[
			applyCommand,
			focus,
			isActive
		]
	);
}